import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import { KpiSticker } from "../../pages/Home/components/KpiSticker";
import { useSummaryStats } from "../../store/useSummaryStats";
import { useDropLocations } from "../../store/useDropLocations";

const quarterOptions: { value: "Q1" | "Q2" | "Q3" | "Q4"; label: string }[] = [
  { value: "Q1", label: "Q1" },
  { value: "Q2", label: "Q2" },
  { value: "Q3", label: "Q3" },
  { value: "Q4", label: "Q4" },
];

const getWeekOptions = (year: number) => {
  const lastDayOfYear = dayjs(`${year}-12-31`);
  const totalWeeks =
    lastDayOfYear.isoWeek() === 1 ? 52 : lastDayOfYear.isoWeek();
  const currentWeek = dayjs().isoWeek();
  const currentYear = dayjs().year();

  return Array.from({ length: totalWeeks }, (_, i) => {
    const weekNumber = i + 1;
    const startOfWeek = dayjs()
      .year(year)
      .isoWeek(weekNumber)
      .startOf("isoWeek");
    const endOfWeek = dayjs().year(year).isoWeek(weekNumber).endOf("isoWeek");
    return {
      value: weekNumber,
      label: `Week ${weekNumber}: ${startOfWeek.format(
        "MMM DD"
      )} - ${endOfWeek.format("MMM DD")}`,
      disabled: year === currentYear && weekNumber > currentWeek,
    };
  });
};

const KeyPerformanceIndicators = () => {
  const { kpiStats, getKPIStats, isKpiLoading } = useSummaryStats();
  const { dropLocations, getDropLocations } = useDropLocations();

  // City filter applied on top of the period. Empty string = "All cities" (default,
  // unfiltered KPI). When a city is picked we resolve it to the matching location
  // ids and pass them through the existing `selectedStores` parameter — backend
  // already understands store-id filtering, so this needs no API change.
  const [city, setCity] = useState<string>("");

  useEffect(() => {
    getDropLocations();
  }, [getDropLocations]);

  const cityOptions = useMemo(() => {
    if (!dropLocations?.length) return [];
    const cities = Array.from(
      new Set(
        dropLocations
          .map((loc) => loc.city)
          .filter((c): c is string => Boolean(c))
      )
    );
    return cities.sort((a, b) => a.localeCompare(b));
  }, [dropLocations]);

  const selectedStoresForCity = useMemo(() => {
    if (!city) return undefined;
    const ids = (dropLocations ?? [])
      .filter((loc) => loc.city === city)
      .map((loc) => loc._id)
      .filter((id): id is string => Boolean(id));
    return ids.length ? ids : undefined;
  }, [city, dropLocations]);

  const currentYear = dayjs().year();
  const currentMonth = dayjs().month() + 1;

  const [period, setPeriod] = useState<
    "all" | "yearly" | "quarterly" | "monthly" | "weekly"
  >("all");
  const [fromYear, setFromYear] = useState<number>(currentYear - 1);
  const [toYear, setToYear] = useState<number>(currentYear);
  const [fromMonth, setFromMonth] = useState<number>(1);
  const [toMonth, setToMonth] = useState<number>(currentMonth);
  const [quarter, setQuarter] = useState<"Q1" | "Q2" | "Q3" | "Q4">("Q1");
  const [fromWeek, setFromWeek] = useState<number>(1);
  const [toWeek, setToWeek] = useState<number>(dayjs().isoWeek());

  const yearOptions = useMemo(
    () => Array.from({ length: currentYear - 2024 + 1 }, (_, i) => 2024 + i),
    [currentYear]
  );

  const monthOptions = [
    { value: 1, label: "Jan" },
    { value: 2, label: "Feb" },
    { value: 3, label: "Mar" },
    { value: 4, label: "Apr" },
    { value: 5, label: "May" },
    { value: 6, label: "Jun" },
    { value: 7, label: "Jul" },
    { value: 8, label: "Aug" },
    { value: 9, label: "Sep" },
    { value: 10, label: "Oct" },
    { value: 11, label: "Nov" },
    { value: 12, label: "Dec" },
  ];

  const fromWeekOptions = useMemo(() => getWeekOptions(fromYear), [fromYear]);
  const toWeekOptions = useMemo(() => getWeekOptions(toYear), [toYear]);

  useEffect(() => {
    if (period === "all") {
      getKPIStats({
        period: "all",
        selectedStores: selectedStoresForCity,
      });
    } else if (period === "yearly") {
      getKPIStats({
        period: "yearly",
        fromYear,
        toYear,
        selectedStores: selectedStoresForCity,
      });
    } else if (period === "monthly") {
      getKPIStats({
        period: "monthly",
        fromYear,
        toYear,
        fromMonth,
        toMonth,
        selectedStores: selectedStoresForCity,
      });
    } else if (period === "weekly") {
      getKPIStats({
        period: "weekly",
        fromYear,
        toYear,
        fromWeek,
        toWeek,
        selectedStores: selectedStoresForCity,
      });
    } else if (period === "quarterly") {
      getKPIStats({
        period: "quarterly",
        year: fromYear,
        quarter,
        selectedStores: selectedStoresForCity,
      });
    }
  }, [
    period,
    fromYear,
    toYear,
    fromMonth,
    toMonth,
    fromWeek,
    toWeek,
    quarter,
    selectedStoresForCity,
    getKPIStats,
  ]);

  // Clamp months when year is the current year so we never select a future month
  useEffect(() => {
    if (period !== "monthly") return;

    if (fromYear === currentYear && fromMonth > currentMonth) {
      setFromMonth(currentMonth);
    }
    if (toYear === currentYear && toMonth > currentMonth) {
      setToMonth(currentMonth);
    }
  }, [period, fromYear, toYear, fromMonth, toMonth, currentYear, currentMonth]);

  const cumulative = kpiStats?.nonCumulative; // align with SummaryScreenV2 usage

  return (
    <section className="w-full rounded-xl bg-white drop-shadow-none p-2 sm:p-7 mb-1 sm:mb-10">
      <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
        Key Performance Indicators
      </p>

      <div className="flex flex-col sm:flex-row items-end  justify-end gap-4 ">
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-1 sm:gap-2">
            <Menu>
              <MenuHandler>
                <button
                  className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                  title={city || "All cities"}
                >
                  {city || "All cities"}
                  <ChevronDown className="ml-1 sm:ml-2 flex-shrink-0" />
                </button>
              </MenuHandler>
              <MenuList
                className="rounded-none max-h-[250px] overflow-y-auto"
                placeholder={undefined}
              >
                <MenuItem
                  disabled={city === ""}
                  onClick={() => setCity("")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  All cities
                </MenuItem>
                {cityOptions.map((c) => (
                  <MenuItem
                    key={c}
                    disabled={city === c}
                    onClick={() => setCity(c)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {c}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <Menu>
              <MenuHandler>
                <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                  {period === "all"
                    ? "All time"
                    : period === "monthly"
                      ? "Monthly"
                      : period === "weekly"
                        ? "Weekly"
                        : period === "quarterly"
                          ? "Quarterly"
                          : "Yearly"}
                  <ChevronDown className="ml-1 sm:ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                <MenuItem
                  disabled={period === "monthly"}
                  onClick={() => setPeriod("monthly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Monthly
                </MenuItem>
                <MenuItem
                  disabled={period === "weekly"}
                  onClick={() => setPeriod("weekly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Weekly
                </MenuItem>
                <MenuItem
                  disabled={period === "quarterly"}
                  onClick={() => setPeriod("quarterly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Quarterly
                </MenuItem>
                <MenuItem
                  disabled={period === "yearly"}
                  onClick={() => setPeriod("yearly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Yearly
                </MenuItem>
              </MenuList>
            </Menu>
          </div>

          {period !== "all" && (
            <>
              <div className="flex items-center gap-1 sm:gap-2">
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                      {fromYear}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {yearOptions.map((y) => (
                      <MenuItem
                        key={y}
                        disabled={fromYear === y || y > toYear}
                        onClick={() => setFromYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                {period === "monthly" && (
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                        {monthOptions.find((m) => m.value === fromMonth)
                          ?.label ?? "Month"}
                        <ChevronDown className="ml-1 sm:ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {monthOptions.map((m) => (
                        <MenuItem
                          key={m.value}
                          disabled={
                            (fromYear === toYear &&
                              (fromMonth === m.value || m.value > toMonth)) ||
                            (fromYear === currentYear && m.value > currentMonth)
                          }
                          onClick={() => setFromMonth(m.value)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {m.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                )}
                {period === "weekly" && (
                  <Menu>
                    <MenuHandler>
                      <button
                        className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                        title={
                          fromWeekOptions.find((w) => w.value === fromWeek)
                            ?.label
                        }
                      >
                        {fromWeekOptions.find((w) => w.value === fromWeek)
                          ?.label || "Week"}
                        <ChevronDown className="ml-1 sm:ml-2 flex-shrink-0" />
                      </button>
                    </MenuHandler>
                    <MenuList
                      className="rounded-none max-h-[250px] overflow-y-auto"
                      placeholder={undefined}
                    >
                      {fromWeekOptions.map((w) => (
                        <MenuItem
                          key={w.value}
                          disabled={w.disabled || fromWeek === w.value}
                          onClick={() => setFromWeek(w.value)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {w.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                )}
                {period === "quarterly" && (
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                        {quarterOptions.find((q) => q.value === quarter)
                          ?.label || "Q1"}
                        <ChevronDown className="ml-1 sm:ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {quarterOptions.map((q) => (
                        <MenuItem
                          key={q.value}
                          disabled={quarter === q.value}
                          onClick={() => setQuarter(q.value)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {q.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                )}
              </div>

              {period !== "quarterly" && (
                <div className="flex items-center gap-1 sm:gap-2">
                  <span className="font-satoshi text-xs sm:text-sm text-gray-text">
                    To:
                  </span>
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                        {toYear}
                        <ChevronDown className="ml-1 sm:ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {yearOptions.map((y) => (
                        <MenuItem
                          key={y}
                          disabled={toYear === y || y < fromYear}
                          onClick={() => setToYear(y)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {y}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                  {period === "monthly" && (
                    <Menu>
                      <MenuHandler>
                        <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                          {monthOptions.find((m) => m.value === toMonth)
                            ?.label ?? "Month"}
                          <ChevronDown className="ml-1 sm:ml-2" />
                        </button>
                      </MenuHandler>
                      <MenuList placeholder={undefined}>
                        {monthOptions.map((m) => (
                          <MenuItem
                            key={m.value}
                            disabled={
                              (fromYear === toYear &&
                                (toMonth === m.value || m.value < fromMonth)) ||
                              (toYear === currentYear && m.value > currentMonth)
                            }
                            onClick={() => setToMonth(m.value)}
                            className="font-satoshi text-base text-dark"
                            placeholder={undefined}
                          >
                            {m.label}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  )}
                  {period === "weekly" && (
                    <Menu>
                      <MenuHandler>
                        <button
                          className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                          title={
                            toWeekOptions.find((w) => w.value === toWeek)?.label
                          }
                        >
                          {toWeekOptions.find((w) => w.value === toWeek)
                            ?.label || "Week"}
                          <ChevronDown className="ml-1 sm:ml-2 flex-shrink-0" />
                        </button>
                      </MenuHandler>
                      <MenuList
                        className="rounded-none max-h-[250px] overflow-y-auto"
                        placeholder={undefined}
                      >
                        {toWeekOptions.map((w) => (
                          <MenuItem
                            key={w.value}
                            disabled={w.disabled || toWeek === w.value}
                            onClick={() => setToWeek(w.value)}
                            className="font-satoshi text-base text-dark"
                            placeholder={undefined}
                          >
                            {w.label}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  )}
                </div>
              )}
            </>
          )}

          <button
            type="button"
            className={`h-9 px-4 rounded-lg font-satoshi font-semibold text-sm shadow-sm transition-all duration-200 cursor-pointer hover:shadow-md active:scale-[0.98] ${
              period === "all"
                ? "bg-droppie-oranje border-2 border-droppie-oranje text-white hover:bg-droppie-oranje-dark"
                : "bg-white border-2 border-gray-300 text-gray-700 hover:border-droppie-oranje hover:text-droppie-oranje"
            }`}
            onClick={() =>
              setPeriod((prev) => (prev === "all" ? "monthly" : "all"))
            }
            aria-pressed={period === "all"}
            aria-label="Toggle All time"
          >
            ALL TIME
          </button>
        </div>
      </div>

      <p className="font-satoshi font-semibold text-xs sm:text-sm mb-4">
        Cumulative
      </p>

      {isKpiLoading ? (
        <div className="flex items-center justify-center py-8">
          <svg
            aria-hidden="true"
            className="w-8 h-8 text-gray-200 animate-spin fill-primary-base"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
        </div>
      ) : cumulative ? (
        <div className="grid grid-cols-1 xs:grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7 3xl:grid-cols-6 gap-4">
          <KpiSticker
            title="App Downloads"
            value={Math.round(cumulative.downloads ?? 0)}
            subtitle="Cumulative downloads"
            color="blue"
            icon={
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            }
          />
          <KpiSticker
            title="Regular Droppers"
            value={Math.round(cumulative.regularDroppers ?? 0)}
            subtitle="Cumulative regular droppers"
            color="green"
            icon={
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            }
          />
          {/* <KpiSticker
            title="Visitors"
            value={cumulative.monthlyVisitors ?? 0}
            subtitle="Cumulative visitors"
            color="purple"
            icon={
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            }
          /> */}
          <KpiSticker
            title="Money Paid Out"
            value={`${new Intl.NumberFormat("nl-NL", {
              style: "currency",
              currency: "EUR",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(Math.round(Number(cumulative.moneyPaidOut ?? 0) / 100))}`}
            subtitle="Cumulative customer payouts"
            color="orange"
            icon={
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            }
          />
          <KpiSticker
            title="Total Drops"
            value={Math.round(cumulative.totalDrops ?? 0)}
            subtitle="Cumulative drops made"
            color="indigo"
            icon={
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                />
              </svg>
            }
          />
          <KpiSticker
            title="Total Weight"
            value={`${(
              Number(cumulative.totalCollectedWeight ?? 0) / 1000
            ).toLocaleString("nl-NL", {
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            })} t`}
            subtitle="Cumulative collected materials (tonnes)"
            color="red"
            icon={
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"
                />
              </svg>
            }
          />
          <KpiSticker
            title="Donation Accounts"
            value={Math.round(cumulative.totalDonationAccounts ?? 0)}
            subtitle="Total accounts that donated"
            color="teal"
            icon={
              <svg
                className="w-6 h-6"
                fill="currentColor"
                stroke="none"
                viewBox="0 0 24 24"
              >
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </svg>
            }
          />
          <KpiSticker
            title="Total money donated"
            value={`${new Intl.NumberFormat("nl-NL", {
              style: "currency",
              currency: "EUR",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(
              Number(
                cumulative.totalMoneyDonated ??
                  kpiStats?.cumulative?.totalMoneyDonated ??
                  0
              ) / 100
            )}`}
            subtitle="Total money donated"
            color="amber"
            icon={
              <svg
                className="w-6 h-6"
                fill="currentColor"
                stroke="none"
                viewBox="0 0 24 24"
              >
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </svg>
            }
          />
        </div>
      ) : (
        <p className="font-satoshi text-sm text-gray-500">
          No KPI data for the selected period.
        </p>
      )}
    </section>
  );
};

export default KeyPerformanceIndicators;
